import { FetchError } from 'ofetch'
import type { User } from '~/types/users'
import type { Session } from '~/types/auth'
import {
  isMFAAuthenticatePending,
  type AllauthAuthResponse,
} from '~/types/allauth'
// MFA CONFIGURATIONS

const NOT_ENFORCED_AND_NOT_EXEMPT = (session: Session) =>
  !session.data.mfa.enforced &&
  !session.data.mfa.setup_required &&
  !session.data.mfa.exempt
const NOT_ENFORCED_AND_USER_ENABLED_MFA = (session: Session) =>
  !session.data.mfa.enforced && session.data.mfa.configured
const ENFORCED_AND_NOT_CONFIGURED = (session: Session) =>
  session.data.mfa.enforced && !session.data.mfa.configured
const ENFORCED_AND_USER_ENABLED_MFA_AND_AUTHENTICATED = (session: Session) =>
  session.data.mfa.enforced &&
  session.data.mfa.configured &&
  session.meta.is_authenticated

export type MFAStatus =
  | 'NO_SETUP_REQUIRED'
  | 'SETUP_REQUIRED'
  | 'CODE_VERIFICATION_REQUIRED'
  | undefined

const useUser = () => useState<User | null>('user', () => null)

export function useAuth() {
  const { $api } = useNuxtApp()
  const user = useUser()

  async function fetchUser(): Promise<MFAStatus | undefined> {
    try {
      const session = await $api<Session>('/_allauth/browser/v1/auth/session', {
        ignoreStatusCodes: [401],
      })

      // get mfa information from session
      const mfaStatus: MFAStatus = checkMFAStatus(session)
      if (
        mfaStatus === 'NO_SETUP_REQUIRED' ||
        ENFORCED_AND_USER_ENABLED_MFA_AND_AUTHENTICATED(session)
      ) {
        user.value = await $api<User>('/contacts/me')
      }

      if (mfaStatus === 'SETUP_REQUIRED') {
        return mfaStatus
      }
    } catch (e) {
      if (e instanceof FetchError && e.statusCode === 401) {
        if (
          isMFAAuthenticatePending(e.response?._data as AllauthAuthResponse)
        ) {
          return 'CODE_VERIFICATION_REQUIRED'
        }
      }
      clearUser()
      return undefined
    }
  }

  function clearUser() {
    user.value = null
  }

  async function logout(options?: { redirect?: boolean }) {
    const redirect = options?.redirect ?? true
    try {
      await $api('_allauth/browser/v1/auth/session', {
        method: 'DELETE',
        ignoreStatusCodes: [401],
      })
    } catch {
      // We expect an error, because allauth will return a 401 ...
    } finally {
      // Always clear user, regardless of API call success
      clearUser()
      // The dedicated /logout page shows a confirmation + countdown before
      // navigating itself, so it opts out of the immediate redirect here.
      if (redirect) {
        await navigateTo('/login', { replace: true })
      }
    }
  }

  function buildProviderCallbackUrl(): string {
    return `${location.protocol}//${location.host}/login/social/callback`
  }

  function checkMFAStatus(session: Session): MFAStatus {
    // Exempt sessions (SSO login, superuser) never need MFA setup, even when
    // enforcement is on and the user has no TOTP. The backend's `exempt` flag is
    // session-method based, so trust it before evaluating enforcement.
    if (session.data.mfa.exempt) {
      return 'NO_SETUP_REQUIRED'
    }
    // check mfa
    if (NOT_ENFORCED_AND_NOT_EXEMPT(session)) {
      // do nothing
      return 'NO_SETUP_REQUIRED'
    }
    if (NOT_ENFORCED_AND_USER_ENABLED_MFA(session)) {
      // user is not exempt and has mfa enabled, but mfa is not enforced -> show code verification
      return 'CODE_VERIFICATION_REQUIRED'
    }
    if (ENFORCED_AND_NOT_CONFIGURED(session)) {
      // user is exempt and has mfa enabled, but mfa is enforced -> show mfa setup modal
      return 'SETUP_REQUIRED'
    }

    return undefined
  }

  return {
    user: readonly(user),
    logout,
    fetchUser,
    clearUser,
    buildProviderCallbackUrl,
  }
}
